/**
 * GrievanceSubmitDialog.jsx — Feb 2026. Standalone, reusable "Raise a
 * grievance" form, extracted so it can be opened from BOTH the full
 * Grievances page (My HR → Grievances, admin-only tab) AND the Dashboard
 * quick-actions bar / "My Grievances" widget — the whole point of this
 * extraction is that regular employees never need to visit an admin-gated
 * tab just to file one. Submission itself stays fully anonymous — see
 * POST /grievances on the backend, which never stores the user's id.
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck } from "lucide-react";

const CATS = ["general", "manager", "compensation", "harassment", "tooling", "other"];
const EMPTY = { title: "", body: "", category: "general" };

export default function GrievanceSubmitDialog({ open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Add a title and details first"); return; }
    setSaving(true);
    try {
      await api.post("/grievances", form);
      toast.success("Submitted anonymously. Founders will see it; your identity is not stored.");
      setForm(EMPTY);
      onOpenChange(false);
      // Feb 2026 fix — a lightweight cross-component refresh signal so the
      // Dashboard's "My Grievances" card updates even when the user
      // submitted via the STICKY HEADER's separate dialog instance (not
      // the card's own "Submit" button), without needing a shared store.
      window.dispatchEvent(new CustomEvent("grievance:submitted"));
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="grievance-submit-dialog">
        <DialogHeader><DialogTitle className="font-display">Anonymous grievance</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-slate-500 flex items-start gap-1.5">
          <ShieldCheck size={14} className="text-[#F54800] shrink-0 mt-0.5" /> Only the founders will see this. Your identity is never attached to the submission.
        </DialogDescription>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="grievance-dash-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input className="rounded-sm" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="grievance-dash-title" />
          <Textarea className="rounded-sm" rows={5} placeholder="Tell us what happened. Be as specific as you feel safe being." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="grievance-dash-body" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="grievance-dash-submit">
            {saving ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
